/**
 * Chart PACK download + offline storage (Capacitor Filesystem).
 *
 * Downloading a pack saves each of its PMTiles to the device's app data
 * directory under chartpacks/<packId>/. Once saved, the charts are served
 * offline: each one becomes a RasterChart whose `path` is the on-device file URI
 * and whose `source` is 'local', which the PMTiles loader resolves with
 * Capacitor.convertFileSrc so the WebView can range-read it with no network.
 *
 * The download itself goes through Filesystem.downloadFile on a real device.
 * That uses the OS HTTP stack, NOT the WebView's fetch, so it steps around the
 * CORS + range-request wall that blocks the WebView from reading GitHub's
 * release-asset host directly. On web (dev build) we fall back to a plain
 * fetch + writeFile, which is fine because there's no native stack to use and
 * the dev host serves the files with permissive CORS.
 *
 * The per-file work is behind a small injectable PackDownloader interface, so the
 * orchestration in downloadPack (the per-file loop, progress aggregation, skipping
 * files already on disk, error handling) is unit-testable with a fake and never
 * has to touch Capacitor.
 */
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import type { CellMeta, ChartFeatureCollection, ChartSource, ChartSourceKind } from '@/types';
import type { ChartPack, RasterChart } from './providers';
import { chartFileName, joinUrl, mapPackCharts } from './packManifest';
import { authedPackUrl } from './chartAccess';

const ROOT = 'chartpacks';
const DIR = Directory.Data;

function packDir(pack: ChartPack): string {
  return `${ROOT}/${pack.id}`;
}
function packFilePath(pack: ChartPack, fileName: string): string {
  return `${packDir(pack)}/${fileName}`;
}

/** Whether offline download is actually usable here (a real Capacitor platform). */
export function canDownload(): boolean {
  // Native is the real path: Filesystem writes to the device's own storage, which
  // is what makes a saved pack survive with no signal.
  if (Capacitor.isNativePlatform()) return true;
  // A dev build allows it on web too. The web downloader underneath is real
  // (fetch, then writeFile through Capacitor's IndexedDB shim), so the whole
  // download-and-read-back path can be exercised in a browser with the network
  // switched off. Without this the only way to test any of it is to build an APK,
  // which is a large part of why it stayed unverified as long as it did.
  //
  // Production web still says "Download in the app". That is not because it
  // cannot work, but because a browser can evict IndexedDB whenever it likes, and
  // the copy next to this button promises something reliable afloat.
  return import.meta.env.DEV;
}

/**
 * Turn whatever Filesystem.readFile handed back into text.
 *
 * Asking for Encoding.UTF8 is not enough, because on web Capacitor's Filesystem
 * ignores it. The read is literally:
 *
 *   async readFile(options) {
 *     // const encoding = options.encoding;
 *     const entry = await this.dbRequest('get', [path]);
 *     return { data: entry.content ? entry.content : '' };
 *   }
 *
 * It returns exactly what was stored. The downloader stores a cell as base64,
 * because that is how writeFile accepts binary and the same path has to carry
 * PMTiles. So native, which honours the encoding, hands back the JSON, and a
 * browser hands back base64, and JSON.parse throws on every cell in the pack. The
 * pack reports itself downloaded and draws nothing.
 *
 * Decoding goes through TextDecoder rather than atob alone. atob yields Latin-1
 * bytes, which would quietly mangle every accented name in the chart: Isla de
 * Culebra, Kaua'i, Bahía. Wrong labels on a chart are worse than no labels,
 * because nobody thinks to doubt them.
 */
export async function fileText(data: string | Blob): Promise<string> {
  if (typeof data !== 'string') {
    // Blob.text() is the obvious call and is not everywhere. It is absent in jsdom
    // and in older Android WebViews, so fall back to the bytes, which is the same
    // work by a longer route.
    if (typeof data.text === 'function') return data.text();
    return new TextDecoder().decode(await data.arrayBuffer());
  }
  // JSON always opens with { or [, and base64 never does, so one character
  // settles which of the two we were given without parsing megabytes to find out.
  const first = data.trimStart()[0];
  if (first === '{' || first === '[') return data;
  try {
    const bin = atob(data);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    // Not base64 either. Hand it back and let the caller's parse report it.
    return data;
  }
}

/** ArrayBuffer -> base64, chunked so a multi-MB PMTiles doesn't blow the stack. */
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export interface DownloadProgress {
  /** Files finished so far. */
  done: number;
  /** Total files in the pack. */
  total: number;
  /** Aggregate bytes written across the whole pack so far. */
  bytes: number;
  /** The file currently being written (undefined once the pack is finished). */
  file?: string;
  /** Bytes written for the current file so far. */
  fileBytes?: number;
  /** Total bytes for the current file (0 if the host didn't say). */
  fileTotal?: number;
}

/**
 * Per-file storage operations, injectable so downloadPack's orchestration can be
 * tested with a fake. The real implementation is chosen by defaultDownloader():
 * native (Filesystem.downloadFile, CORS-free) on a device, fetch+writeFile on web.
 */
export interface PackDownloader {
  /** Make sure the pack's directory exists. */
  ensureDir(dir: string): Promise<void>;
  /** File names already saved under dir, mapped to their byte size (0 if unknown). */
  existing(dir: string): Promise<Map<string, number>>;
  /**
   * Download one file to `path` (relative to Directory.Data). Reports this file's
   * OWN cumulative byte progress via onBytes(bytesSoFar, contentLength). Resolves
   * with the number of bytes written. Throws if the download fails.
   */
  fetchToFile(args: {
    url: string;
    path: string;
    onBytes?: (bytesSoFar: number, contentLength: number) => void;
  }): Promise<number>;
}

/**
 * Read the files already on disk under dir, with sizes where available. No
 * extension filter: a pack is PMTiles or GeoJSON cells depending on its kind,
 * and every caller checks for the names it actually wants anyway.
 */
async function readExisting(dir: string): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  try {
    const { files } = await Filesystem.readdir({ path: dir, directory: DIR });
    // Capacitor 6 returns FileInfo[] (name + size); older typings returned string[].
    for (const f of files as Array<string | { name: string; size?: number }>) {
      const name = typeof f === 'string' ? f : f.name;
      const size = typeof f === 'string' ? 0 : typeof f.size === 'number' ? f.size : 0;
      out.set(name, size);
    }
  } catch {
    /* directory doesn't exist yet — nothing downloaded */
  }
  return out;
}

/** Size of a saved file on device, or 0 if it can't be read. */
async function statSize(path: string): Promise<number> {
  try {
    const { size } = await Filesystem.stat({ path, directory: DIR });
    return typeof size === 'number' ? size : 0;
  } catch {
    return 0;
  }
}

/** Real downloader for a device: native HTTP via Filesystem.downloadFile. */
function nativeDownloader(): PackDownloader {
  return {
    async ensureDir(dir) {
      await Filesystem.mkdir({ path: dir, directory: DIR, recursive: true }).catch(() => {
        /* already exists — fine */
      });
    },
    existing: readExisting,
    async fetchToFile({ url, path, onBytes }) {
      // downloadFile emits 'progress' events (bytes / contentLength). We download
      // one file at a time, so there's only ever one in flight — no need to filter
      // events by url (and the url may be a post-redirect one anyway).
      let lastBytes = 0;
      let lastTotal = 0;
      let handle: PluginListenerHandle | undefined;
      if (onBytes) {
        handle = await Filesystem.addListener('progress', (p) => {
          lastBytes = p.bytes;
          lastTotal = p.contentLength;
          onBytes(p.bytes, p.contentLength);
        });
      }
      try {
        await Filesystem.downloadFile({
          url,
          path,
          directory: DIR,
          progress: true,
          recursive: true,
        });
      } finally {
        await handle?.remove();
      }
      if (lastTotal > 0) return lastTotal;
      if (lastBytes > 0) return lastBytes;
      return statSize(path);
    },
  };
}

/** Fallback downloader for the web/dev build: WebView fetch + base64 writeFile. */
function webDownloader(): PackDownloader {
  return {
    async ensureDir(dir) {
      await Filesystem.mkdir({ path: dir, directory: DIR, recursive: true }).catch(() => {
        /* already exists — fine */
      });
    },
    existing: readExisting,
    async fetchToFile({ url, path, onBytes }) {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed for ${chartFileName(path)}: HTTP ${res.status}`);
      const declared = Number(res.headers.get('content-length')) || 0;
      const buf = await res.arrayBuffer();
      onBytes?.(buf.byteLength, declared || buf.byteLength);
      await Filesystem.writeFile({
        path,
        directory: DIR,
        data: toBase64(buf),
        recursive: true,
      });
      return buf.byteLength;
    },
  };
}

/** The right downloader for where we're running (native device vs web/dev). */
export function defaultDownloader(): PackDownloader {
  return Capacitor.isNativePlatform() ? nativeDownloader() : webDownloader();
}

export interface DownloadPackOptions {
  /** Called as each file makes progress and as each file finishes. */
  onProgress?: (p: DownloadProgress) => void;
  /** Injected for tests; defaults to the native/web downloader for this platform. */
  downloader?: PackDownloader;
}

/**
 * Download a list of files from a pack's host to device storage. Files already on
 * disk are skipped (so a re-run resumes rather than re-fetching), and their sizes
 * still count toward the aggregate byte progress. Throws if any file can't be
 * fetched or written, so the caller can surface a failure.
 *
 * Both kinds of pack go through here: raster packs pass their PMTiles names,
 * vector packs pass their cell file names. The only difference between them is
 * what is inside the files.
 */
export async function downloadFiles(
  pack: ChartPack,
  wanted: PackFile[],
  options: DownloadPackOptions = {},
): Promise<void> {
  const { onProgress, downloader = defaultDownloader() } = options;
  const dir = packDir(pack);
  await downloader.ensureDir(dir);
  const have = await downloader.existing(dir);
  const installed = await readInstalled(pack);
  // Skip only what is both present AND unchanged. Everything else is fetched,
  // which is what turns a re-run into either a resume or a correction download
  // depending on what has moved.
  const fresh = new Set(
    wanted.filter((w) => have.has(w.file) && installed.get(w.file) === w.identity).map((w) => w.file),
  );

  const total = wanted.length;
  let done = 0;
  // Bytes of every file already finished (downloaded now, or skipped as current).
  let completedBytes = 0;

  for (const { file, identity } of wanted) {
    if (fresh.has(file)) {
      completedBytes += have.get(file) ?? 0;
      done += 1;
      onProgress?.({ done, total, bytes: completedBytes, file, fileBytes: have.get(file) ?? 0, fileTotal: have.get(file) ?? 0 });
      continue;
    }

    // authedPackUrl carries the access token onto private-host URLs (no-op
    // for the public NOAA host), so gated packs download like any other.
    const url = authedPackUrl(joinUrl(pack.baseUrl, file));
    const path = packFilePath(pack, file);
    const before = completedBytes;
    const bytes = await downloader.fetchToFile({
      url,
      path,
      onBytes: (fileBytes, fileTotal) =>
        onProgress?.({ done, total, bytes: before + fileBytes, file, fileBytes, fileTotal }),
    });
    completedBytes += bytes;
    done += 1;
    installed.set(file, identity);
    onProgress?.({ done, total, bytes: completedBytes, file, fileBytes: bytes, fileTotal: bytes });
  }

  await writeInstalled(pack, installed);
  // A final tick with no current file, so the caller sees a clean 100%.
  onProgress?.({ done, total, bytes: completedBytes });
}

/** The file a cell is stored under, both on the host and on the device. */
export function cellFileName(cell: CellMeta): string {
  return `${cell.id}.geojson`;
}

/**
 * One file of a pack, and a string that changes whenever its contents do.
 *
 * Presence alone is not enough to decide whether a file is current. A corrected
 * cell keeps its name, so "the file is already there" and "the file is up to
 * date" are different questions, and answering the second with the first is how
 * a plotter ends up drawing last month's chart while reporting itself current.
 */
export interface PackFile {
  file: string;
  identity: string;
}

/** A cell's identity: edition, update number and issue date. */
export function cellIdentity(cell: CellMeta): string {
  return `e${cell.edition ?? 0}u${cell.update ?? 0}i${cell.issued ?? ''}`;
}

/**
 * A raster chart's identity. Antares carries the edition in the chart name
 * ("... , edition 3"), so the name moving at all means the chart moved.
 */
export function chartIdentity(chart: RasterChart): string {
  return chart.name;
}

/** Where the record of what we downloaded lives, inside the pack's own folder. */
const INSTALLED_FILE = 'installed.json';

/** Read the pack's record of what was downloaded: file name -> identity. */
export async function readInstalled(pack: ChartPack): Promise<Map<string, string>> {
  try {
    const { data } = await Filesystem.readFile({
      path: packFilePath(pack, INSTALLED_FILE),
      directory: DIR,
      encoding: Encoding.UTF8,
    });
    const parsed = JSON.parse(await fileText(data)) as Record<string, unknown>;
    const out = new Map<string, string>();
    for (const [k, v] of Object.entries(parsed)) if (typeof v === 'string') out.set(k, v);
    return out;
  } catch {
    // No record: we cannot tell how old the files on disk are. See staleFiles.
    return new Map();
  }
}

async function writeInstalled(pack: ChartPack, installed: Map<string, string>): Promise<void> {
  try {
    await Filesystem.writeFile({
      path: packFilePath(pack, INSTALLED_FILE),
      directory: DIR,
      encoding: Encoding.UTF8,
      data: JSON.stringify(Object.fromEntries(installed)),
    });
  } catch {
    /* a pack that cannot record what it holds will simply re-check next time */
  }
}

/**
 * The pack's OWN copy of its manifest, written into the pack directory at
 * download time: the pack entry plus its chart or cell list.
 *
 * WHY a pack carries its own manifest. Until this existed, a downloaded pack
 * could only be brought back by fetching the REMOTE packs list first: the store
 * fetched packs.json, then walked the saved ids and restored the ones it found.
 * So with no signal, the remote list failed, the restore loop never ran, and
 * charts sitting on the device were unreachable. An offline navigation app that
 * loses its downloaded charts the moment it goes offline has the failure exactly
 * the wrong way round, and moving the licensed packs behind a token gate made it
 * bite far more often, because now the list can fail while the network is fine.
 *
 * A pack is a folio. It should carry its own index, the way a chart folio does,
 * and be readable on its own with nothing else present.
 */
const RECORD_FILE = 'pack.json';

export interface PackRecord {
  pack: ChartPack;
  /** Raster packs: the raw chart list (bare file names, as published). */
  charts?: RasterChart[];
  /** Vector packs: the cell list. */
  cells?: CellMeta[];
}

/** Write the pack's own manifest alongside its data. Best-effort. */
export async function writePackRecord(record: PackRecord): Promise<void> {
  try {
    await Filesystem.writeFile({
      path: packFilePath(record.pack, RECORD_FILE),
      directory: DIR,
      encoding: Encoding.UTF8,
      data: JSON.stringify(record),
    });
  } catch {
    /* a pack that cannot record itself falls back to the remote list */
  }
}

/** Read one pack's own manifest off the device. Null when it is not there. */
export async function readPackRecord(packId: string): Promise<PackRecord | null> {
  try {
    const { data } = await Filesystem.readFile({
      path: `${ROOT}/${packId}/${RECORD_FILE}`,
      directory: DIR,
      encoding: Encoding.UTF8,
    });
    const parsed = JSON.parse(await fileText(data)) as PackRecord;
    // Only trust a record that actually carries a usable pack entry: a truncated
    // or half-written file must not resurrect a pack we cannot then read.
    if (!parsed?.pack?.id || !Array.isArray(parsed.pack.bounds)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Which of a pack's files need fetching: anything missing, and anything whose
 * identity has moved on since we saved it.
 *
 * A file present with no recorded identity counts as stale. We genuinely do not
 * know how old it is, and in a navigation app "unknown vintage" must not resolve
 * to "assume current". It costs one re-download of a pack saved before this
 * existed, which is the right way round to be wrong.
 */
export function staleFiles(
  wanted: PackFile[],
  present: Set<string> | Map<string, number>,
  installed: Map<string, string>,
): PackFile[] {
  const has = (f: string) => (present instanceof Set ? present.has(f) : present.has(f));
  return wanted.filter((w) => !has(w.file) || installed.get(w.file) !== w.identity);
}

/** How many of a pack's files have been corrected since it was downloaded. */
export function correctionsBehind(wanted: PackFile[], installed: Map<string, string>): number {
  if (installed.size === 0) return 0;
  return wanted.filter((w) => installed.has(w.file) && installed.get(w.file) !== w.identity).length;
}

/** Download every PMTiles in a raster pack. */
export async function downloadPack(
  pack: ChartPack,
  rawCharts: RasterChart[],
  options: DownloadPackOptions = {},
): Promise<void> {
  return downloadFiles(
    pack,
    rawCharts.map((c) => ({ file: chartFileName(c.path), identity: chartIdentity(c) })),
    options,
  );
}

/** Download every cell in a vector pack. */
export async function downloadCellPack(
  pack: ChartPack,
  cells: CellMeta[],
  options: DownloadPackOptions = {},
): Promise<void> {
  return downloadFiles(
    pack,
    cells.map((c) => ({ file: cellFileName(c), identity: cellIdentity(c) })),
    options,
  );
}

/** The file names already saved on device for a pack (empty if none / not downloaded). */
export async function downloadedFiles(pack: ChartPack): Promise<Set<string>> {
  return new Set((await readExisting(packDir(pack))).keys());
}

/** True when every chart file of the pack is present on device. */
export async function isPackDownloaded(pack: ChartPack, rawCharts: RasterChart[]): Promise<boolean> {
  if (rawCharts.length === 0) return false;
  const have = await downloadedFiles(pack);
  return rawCharts.every((c) => have.has(chartFileName(c.path)));
}

/** True when every cell of a vector pack is present on device. */
export async function isCellPackDownloaded(pack: ChartPack, cells: CellMeta[]): Promise<boolean> {
  if (cells.length === 0) return false;
  const have = await downloadedFiles(pack);
  return cells.every((c) => have.has(cellFileName(c)));
}

/**
 * A chart source reading a downloaded vector pack straight off the device.
 *
 * Cells are read with Filesystem.readFile rather than fetched through a file URI.
 * A raster pack can hand the PMTiles loader a URI because that loader range-reads
 * it via Capacitor.convertFileSrc, but a plain fetch of a file:// URL from an
 * https origin is blocked in the WebView, so a cell has to come back through the
 * plugin. The cell list comes from the pack manifest that was saved with it, so
 * this works with no network at all.
 */
export class DownloadedCellSource implements ChartSource {
  readonly id: string;
  readonly name: string;
  readonly kind: ChartSourceKind = 'training';

  /** Same bounded cache as the online source: one cell can be megabytes. */
  private cache = new Map<string, Promise<ChartFeatureCollection>>();
  private readonly maxCached: number;

  constructor(
    private readonly pack: ChartPack,
    private readonly cells: CellMeta[],
    opts: { maxCached?: number } = {},
  ) {
    this.id = `pack:${pack.id}`;
    this.name = pack.region;
    this.maxCached = opts.maxCached ?? 24;
  }

  async listCells(): Promise<CellMeta[]> {
    return this.cells;
  }

  async loadCell(id: string): Promise<ChartFeatureCollection> {
    const held = this.cache.get(id);
    if (held) {
      this.cache.delete(id);
      this.cache.set(id, held);
      return held;
    }
    const promise = this.readCell(id);
    this.cache.set(id, promise);
    // A failed read must not sit in the cache pretending to be a cell.
    promise.catch(() => {
      if (this.cache.get(id) === promise) this.cache.delete(id);
    });
    while (this.cache.size > this.maxCached) {
      const oldest = this.cache.keys().next();
      if (oldest.done) break;
      this.cache.delete(oldest.value);
    }
    return promise;
  }

  private async readCell(id: string): Promise<ChartFeatureCollection> {
    const { data } = await Filesystem.readFile({
      path: packFilePath(this.pack, `${id}.geojson`),
      directory: DIR,
      encoding: Encoding.UTF8,
    });
    return JSON.parse(await fileText(data)) as ChartFeatureCollection;
  }
}

/**
 * The pack's charts as OFFLINE RasterCharts (source 'local', path = device file
 * URI). Resolves the file URI for each via Filesystem.getUri.
 */
export async function localPackCharts(
  pack: ChartPack,
  rawCharts: RasterChart[],
): Promise<RasterChart[]> {
  // Which files are actually here: ONE directory read, rather than asking about
  // each chart in turn. This used to call Filesystem.getUri per chart, awaited
  // one at a time, and each of those is a round trip across the Capacitor bridge.
  // Fine for a pack of twenty. With the full Antares set on the phone it is
  // eight hundred round trips before the app can draw anything, which is the
  // difference between opening in a second and sitting on a blank screen long
  // enough for the skipper to decide the app is dead and force-stop it. It was
  // never dead; it was counting files.
  const have = await downloadedFiles(pack);
  const present = rawCharts.filter((c) => have.has(chartFileName(c.path)));
  if (present.length === 0) return [];

  const uriFor = await packFileUris(
    pack,
    present.map((c) => chartFileName(c.path)),
  );
  const resolved = present.filter((c) => uriFor.has(chartFileName(c.path)));
  return mapPackCharts(pack, resolved, (file) => ({
    path: uriFor.get(file) as string,
    source: 'local',
  }));
}

/**
 * Device URIs for a pack's files, in two calls rather than one per file.
 *
 * getUri is a pure path mapping on the native side, so the URI of a file in a
 * directory is the URI of the directory plus the file name. That is worth
 * exploiting and NOT worth assuming: the platform percent-encodes the path it
 * builds, and a chart whose name needs encoding would come out subtly wrong,
 * pointing the loader at a file that is not there.
 *
 * So it is checked rather than trusted. One call gives the directory, a second
 * resolves a single real file, and if joining the two reproduces that answer
 * exactly then the same join is used for the rest. If it does not, we fall back
 * to asking per file, in parallel batches, which is still far better than the
 * one-at-a-time it replaced.
 */
async function packFileUris(pack: ChartPack, files: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (files.length === 0) return out;

  try {
    const dir = await Filesystem.getUri({ path: packDir(pack), directory: DIR });
    const base = dir.uri.replace(/\/+$/, '');
    const probeName = files[0];
    const probe = await Filesystem.getUri({
      path: packFilePath(pack, probeName),
      directory: DIR,
    });
    if (`${base}/${probeName}` === probe.uri) {
      for (const f of files) out.set(f, `${base}/${f}`);
      return out;
    }
    out.set(probeName, probe.uri);
  } catch {
    /* fall through to asking per file */
  }

  // Batched rather than all at once: the bridge is a queue, and eight hundred
  // calls posted in one go would bury it just as thoroughly as sending them one
  // at a time, only less predictably.
  const remaining = files.filter((f) => !out.has(f));
  const BATCH = 32;
  for (let i = 0; i < remaining.length; i += BATCH) {
    const slice = remaining.slice(i, i + BATCH);
    const uris = await Promise.all(
      slice.map(async (f) => {
        try {
          const { uri } = await Filesystem.getUri({ path: packFilePath(pack, f), directory: DIR });
          return [f, uri] as const;
        } catch {
          return null; // missing file: left out, exactly as before
        }
      }),
    );
    for (const u of uris) if (u) out.set(u[0], u[1]);
  }
  return out;
}

/** Delete a pack's downloaded files from the device. */
export async function deletePack(pack: ChartPack): Promise<void> {
  try {
    await Filesystem.rmdir({ path: packDir(pack), directory: DIR, recursive: true });
  } catch {
    /* nothing to delete */
  }
}
